import { Injector, Service, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxDropdownOptionsSource,
  OnyxFilter,
  OnyxOptional,
  OnyxOptionsGroup,
} from '@onyx/angular';
import {
  ActionHelper,
  OnyxFilterHelper,
  OnyxFilterOperator,
  OnyxModalService,
  OnyxToastService,
} from '@onyx/angular';
import { compact, isString } from 'lodash';
import { map } from 'rxjs';
import { AuthStore } from '../../../../../auth/common/stores/auth.store';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { NavigationHelper } from '../../../../../common/helpers/navigation.helper';
import type { HelperOptions } from '../../../../../common/interfaces/utilities/helper-options';
import type { UniqueField } from '../../../../../common/interfaces/utilities/unique-field';
import { XlsxService } from '../../../../common/services/xlsx.service';
import type { ContractorModalData } from '../../contractor-modal/contractor-modal.component';
import { ContractorModalComponent } from '../../contractor-modal/contractor-modal.component';
import { BlockContractorModalComponent } from '../components/block-contractor-modal/block-contractor-modal.component';
import { CONTRACTORS_EXPORT_DECIMAL_PLACES } from '../constants/contractors-export-decimal-places';
import { getContractorExportData } from '../constants/contractors-export-schema';
import { CONTRACTORS_XLSX_TEMPLATE } from '../constants/contractors-xlsx-template';
import { ContractorCategory } from '../enums/contractor-category';
import { ContractorStatus } from '../enums/contractor-status';
import { ContractorsRoute } from '../enums/contractors-route';
import type {
  Contractor,
  SimplifiedContractor,
} from '../interfaces/contractor';
import { ContractorsService } from '../services/contractors.service';
import type { ContractorFormDto } from './contractor-form.helper';

export const CONTRACTOR_HEADER_ACTIONS = [
  'edit',
  'share',
  'block',
  'unblock',
] as const;

@Service()
export class ContractorHelper {
  private readonly authStore = inject(AuthStore);
  private readonly contractorsService = inject(ContractorsService);
  private readonly router = inject(Router);
  private readonly modalService = inject(OnyxModalService);
  private readonly toastService = inject(OnyxToastService);
  private readonly translateService = inject(TranslateService);
  private readonly xlsxService = inject(XlsxService);
  private readonly injector = inject(Injector);

  private readonly I18N = 'contractors';

  public getContractorsSource(
    showOnlyActive = false,
  ): OnyxDropdownOptionsSource<Contractor> {
    return {
      list: (query, pagination) =>
        this.contractorsService.searchContractors(query, {
          ...pagination,
          filters: compact([
            ContractorHelper.createCategoryFilter(ContractorCategory.ACTIVE),
            showOnlyActive
              ? ContractorHelper.createStatusFilter([ContractorStatus.ACTIVE])
              : null,
          ]),
        }),
      get: (uuid) =>
        this.contractorsService.getContractor(uuid).pipe(
          map((contractor) => ({
            name: contractor.companyProfile.displayName,
            value: contractor,
            leftFlag: contractor.companyProfile.country,
          })),
        ),
      idKey: 'uuid',
    };
  }

  public getOptions(
    contractor: Contractor,
    options: HelperOptions,
    exclude: readonly (typeof CONTRACTOR_HEADER_ACTIONS)[number][] = [],
  ): OnyxOptionsGroup<() => void>[] {
    const hasEdit = this.authStore.hasPermission('contractors.edit');
    const isArchived = ContractorHelper.isArchived(contractor);

    return [
      ...(!exclude.includes('edit') && !isArchived && hasEdit
        ? [
            {
              id: '0.default',
              options: [
                {
                  name: `${this.translateService.instant('labels.edit')}...`,
                  value: () => {
                    this.edit(contractor.uuid, options);
                  },
                  leftIcon: { name: 'edit-outlined', size: 20 },
                } as const,
              ],
            },
          ]
        : []),
      {
        id: '1.actions',
        options: !exclude.includes('share')
          ? [
              {
                name: 'labels.share',
                value: () => this.share(contractor),
                leftIcon: { name: 'share-outlined', size: 20 },
              } as const,
            ]
          : [],
      },
      {
        id: '2.block',
        options: hasEdit
          ? [
              ...(!exclude.includes('block') &&
              contractor.status.value === ContractorStatus.ACTIVE
                ? [
                    {
                      name: `${this.translateService.instant('labels.block')}...`,
                      value: () => this.block(contractor),
                      leftIcon: { name: 'locked-outlined', size: 20 },
                    } as const,
                  ]
                : []),
              ...(!exclude.includes('unblock') &&
              contractor.status.value === ContractorStatus.BLOCKED
                ? [
                    {
                      name: 'labels.unblock',
                      value: () => this.unblock(contractor.uuid),
                      leftIcon: { name: 'unlocked-outlined', size: 20 },
                    } as const,
                  ]
                : []),
            ]
          : [],
      },
      {
        id: '3.archive',
        options: hasEdit
          ? [
              {
                name: 'labels.export',
                value: () => this.export(contractor),
                leftIcon: { name: 'export-outlined', size: 20 },
              } as const,
              ...(contractor.status.value === ContractorStatus.ACTIVE
                ? [
                    {
                      name: 'labels.archive',
                      value: () => this.toggleArchived(contractor, true),
                      leftIcon: { name: 'archives-outlined', size: 20 },
                      leftIconColor: 'red',
                    } as const,
                  ]
                : []),
              ...(isArchived
                ? [
                    {
                      name: 'labels.unarchive',
                      value: () => this.toggleArchived(contractor, false),
                      leftIcon: { name: 'undo-outlined', size: 20 },
                    } as const,
                  ]
                : []),
            ]
          : [],
      },
    ].filter((group) => group.options.length);
  }

  public openModal(contractor: ContractorModalData): void {
    this.modalService.open<ContractorModalData>(
      ContractorModalComponent,
      contractor,
    );
  }

  public share(contractor: SimplifiedContractor): void {
    ActionHelper.share(
      ContractorsRoute.CONTRACTOR_CARD.replace(':uuid', contractor.uuid),
      this.toastService,
    );
  }

  public edit(uuid: string, options: HelperOptions): void {
    this.router.navigateByUrl(
      ContractorsRoute.EDIT_CONTRACTOR.replace(':uuid', uuid),
      NavigationHelper.withOrigin(this.router),
    );
    CommonHelper.handleOptions(uuid, options);
  }

  public block(contractor: Contractor): void {
    this.modalService
      .open<Contractor, boolean>(BlockContractorModalComponent, contractor)
      .result.subscribe();
  }

  public unblock(uuid: string): void {
    this.contractorsService.unblockContractor(uuid).subscribe(() =>
      this.toastService.showSuccess({
        message: `${this.I18N}.toasts.contractorUnblocked`,
      }),
    );
  }

  private export(contractor: Contractor): void {
    this.xlsxService.export(
      {
        template: CONTRACTORS_XLSX_TEMPLATE,
        name: contractor.companyProfile.displayName,
      },
      getContractorExportData(contractor, this.injector),
      CONTRACTORS_EXPORT_DECIMAL_PLACES,
    );
  }

  private toggleArchived(contractor: Contractor, force?: boolean): void {
    force ??= contractor.status.value !== ContractorStatus.ARCHIVED;

    const { uuid } = contractor;
    const action$ = force
      ? this.contractorsService.archiveContractor(uuid)
      : this.contractorsService.unarchiveContractor(uuid);
    const message = force
      ? `${this.I18N}.toasts.contractorArchived`
      : `${this.I18N}.toasts.contractorUnarchived`;

    action$.subscribe(() => this.toastService.showSuccess({ message }));
  }

  public static isArchived(contractor: Contractor | ContractorStatus): boolean {
    const status = isString(contractor) ? contractor : contractor.status.value;
    return status === ContractorStatus.ARCHIVED;
  }

  public static isContractorType(
    contractor: SimplifiedContractor | Contractor,
  ): contractor is Contractor {
    return 'status' in contractor;
  }

  public static getUniqueFields(dto: ContractorFormDto): UniqueField[] {
    const I18N = 'contractors.contractorForm';
    return [
      {
        values: dto.branches.map((branch) => branch.name!),
        key: 'name',
        paramKey: `${I18N}.uniqueErrors.branchName`,
      },
      {
        values: dto.contactPersons.map((contactPerson) => contactPerson.email!),
        key: 'email',
        paramKey: `${I18N}.uniqueErrors.contactPersonEmail`,
      },
      {
        values: dto.contactPersons.map(
          (contactPerson) =>
            `${contactPerson.phone?.areaCode} ${contactPerson.phone?.number}`,
        ),
        key: 'phone',
        paramKey: `${I18N}.uniqueErrors.contactPersonPhone`,
      },
      {
        values: dto.contactPersons
          .map((contactPerson) => contactPerson.transId)
          .filter((transId) => transId != null),
        key: 'transId',
        paramKey: `${I18N}.uniqueErrors.contactPersonTransId`,
      },
      {
        values: dto.contactPersons
          .map((contactPerson) => contactPerson.timocomId)
          .filter((timocomId) => timocomId != null),
        key: 'timocomId',
        paramKey: `${I18N}.uniqueErrors.contactPersonTimocomId`,
      },
    ];
  }

  public static getFilters(
    category: ContractorCategory,
    customFilters: OnyxOptional<OnyxFilter[]>,
  ): OnyxFilter[] {
    return OnyxFilterHelper.merge(
      [{ ...ContractorHelper.createCategoryFilter(category), readonly: true }],
      customFilters,
    );
  }

  public static createCategoryFilter(category: ContractorCategory): OnyxFilter {
    return {
      key: 'category',
      operator: OnyxFilterOperator.EQ,
      value: category,
    };
  }

  public static createStatusFilter(statuses: ContractorStatus[]): OnyxFilter {
    return { key: 'status', operator: OnyxFilterOperator.IN, value: statuses };
  }

  public static createVatIdFilters(
    country: string,
    vatId: string,
  ): OnyxFilter[] {
    return [
      { key: 'country', operator: OnyxFilterOperator.EQ, value: country },
      { key: 'vatId', operator: OnyxFilterOperator.EQ, value: vatId },
    ];
  }
}
